import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { useStaffRole } from '../hooks/useStaffRole';
import { Icon } from '../utils/icons';
import { TICKET_TOPICS, TICKET_ROLES, ownerToneColor, ownerText, topicsFor } from '../constants/ticketRoles';

const hexToRgba = (hex, a) => { const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16); return `rgba(${r},${g},${b},${a})`; };
function KPI({ label, value, iconName, color, muted, caption }) {
  const [showCaption, setShowCaption] = useState(false);
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
      <div style={{ width: '46px', height: '46px', borderRadius: '12px', background: hexToRgba(color, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon name={iconName} size={22} color={color} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: 4 }}>
          <span>{label}</span>
          {/* ⚠️ A BUTTON, NOT A title= TOOLTIP. A native tooltip needs hover, and
              there is no hover on a phone — iOS Safari shows nothing at all on
              long-press, and Android Chrome shows nothing on tap. A caveat
              delivered only by title= is invisible to every touch user, which
              for a card whose whole point is "this number does not measure what
              you think" is the wrong thing to lose.

              ⚠️ TAP TARGET: the glyph is 13px but the button is padded to 24×24
              and sits inside the label row, so it does not overlap the card's
              own tap area — nothing else on a KPI card is clickable, so there
              is no target to steal. 24px is below the 44px WCAG 2.5.5 target
              size; it is a supplementary disclosure rather than a primary
              control, and it stays where a caption would have been rather than
              floating, so a mis-tap lands on inert card. */}
          {caption && (
            <button type="button" onClick={() => setShowCaption((v) => !v)}
              aria-expanded={showCaption} aria-label={showCaption ? 'Hide details' : 'Show details'}
              title={caption}
              style={{ width: 24, height: 24, marginLeft: -2, padding: 0, border: 'none', background: 'none',
                color: showCaption ? '#3a6ad9' : '#9ca3af', cursor: 'pointer', fontSize: 13, lineHeight: 1,
                fontWeight: 700, flexShrink: 0 }}>ⓘ</button>
          )}
        </div>
        {/* ⚠️ AN ABSENCE IS NOT A VALUE, so it is not set at value size. "No
            ratings yet" rendered in 26px black read as a headline finding and
            crowded the card; the point of the empty state is that there is
            nothing to report. Caption size, muted, and short. */}
        {muted
          ? <div style={{ fontSize: '13px', fontWeight: '600', color: '#9ca3af', lineHeight: 1.3 }}>{value}</div>
          : <div style={{ fontSize: '26px', fontWeight: '900', color: '#111827', lineHeight: 1 }}>{value}</div>}
        {/* ⚠️ COLLAPSED BY DEFAULT SO THE CARDS KEEP EQUAL HEIGHTS. A paragraph
            of limitations inside a KPI card made one card three lines taller
            than its neighbours; the caveat still has to exist, so it moves
            behind the disclosure rather than being deleted. */}
        {caption && showCaption && (
          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: 6, lineHeight: 1.45 }}>{caption}</div>
        )}
      </div>
    </div>
  );
}

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
// ⚠️ THREE TABS, DERIVED FROM owner_role. These are the only values it can hold
// now that `engineering` exists as a staff role — before that, the third tab had
// no role behind it and could never have contained a ticket.
// One rung of the funnel. Indentation is the denominator: a reader sees the
// nesting before reading a word.
function FunnelRow({ indent, label, n, pct, tone, info }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', padding: '4px 0', paddingLeft: indent * 22 }}>
      <span style={{ color: '#9ca3af', marginRight: 6 }}>{indent > 0 ? '└─' : ''}</span>
      <span style={{ color: tone || '#374151', fontWeight: indent === 0 ? 700 : 500 }}>{label}</span>
      {/* ⚠️ THE EXCLUSION DISCLOSURE LIVES HERE NOW — demoted, not deleted.
          It was a paragraph under the funnel, and it earned its place: written
          as a FILTER inside the query that had already removed test rows, it
          reported "0 excluded" while excluding 2. A funnel that quietly drops
          rows is indistinguishable from one that had fewer rows, so the count
          of what was dropped travels with the numbers.

          Reduced to an (i) on the row it qualifies, and rendered only when the
          count is non-zero — on a clean week nothing shows at all. If Hannah
          wants it gone entirely now she has seen this, that is her call; what
          must not happen is the numbers silently excluding rows with nothing
          on screen saying so. */}
      {info && (
        <span title={info} style={{
          marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#9ca3af',
          border: '1px solid #e5e7eb', borderRadius: 9999, width: 14, height: 14,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'help', flexShrink: 0,
        }}>i</span>
      )}
      <span style={{ flex: 1, borderBottom: '1px dotted #e5e7eb', margin: '0 8px' }} />
      {/* ⚠️ THE COUNT IS LAST, SO IT IS FLUSH WITH THE CARD'S RIGHT PADDING.
          Deleting the annotation column made the counts align with each OTHER —
          verified in the deployed bundle — but they still sat 56px inside the
          card edge, parked behind a percent column that is fixed-width and,
          below the n=5 floor, empty on every row. Aligned with each other and
          inset from everything else still reads as misaligned, because the eye
          compares the column to the card edge and to the flush-right control
          in the header above it.

          Percent moved to the LEFT: it is the secondary figure and it is absent
          far more often than present. The count is the column a reader scans
          down, so the count is the one anchored to the edge. Nothing may be
          added after this span, and it carries no right margin or padding. */}
      {/* ⚠️ SUPPRESSED IS A STATE, AND IT SAYS SO. An empty column reads as a
          feature that was never built; this column is deliberately blank below
          the floor, and silence made a decision look like an omission. Today is
          exactly the case it exists for — 1 attempted, 1 confirmed, which as a
          percentage would read "100% resolution" off a single email. */}
      <span title={pct === SUPPRESSED ? `Percentages appear from ${FUNNEL_MIN_N}. Below that a single message moves the figure by a hundred points, so the count is shown on its own.` : undefined}
        style={{ width: 52, textAlign: 'right', flexShrink: 0,
          color: pct === SUPPRESSED ? '#d1d5db' : (tone || '#9ca3af'),
          fontWeight: pct === SUPPRESSED ? 600 : 700,
          fontSize: pct === SUPPRESSED ? 10 : Math.max(10, 12 - indent),
          cursor: pct === SUPPRESSED ? 'help' : undefined }}>
        {pct === SUPPRESSED ? `from ${FUNNEL_MIN_N}` : (pct ?? '')}
      </span>
      {/* ⚠️ THE COUNT SHRINKS WITH DEPTH, so the column reads as a hierarchy
          rather than a list of equally weighted figures. Every number here was
          the same size, which made a sub-count that rolls UP into the row above
          it look like a peer of it — the indentation said "child" and the
          typography said "sibling", and the eye believes the typography. */}
      <span style={{ fontWeight: indent === 0 ? 800 : 700, color: indent === 0 ? '#111827' : '#374151',
        fontSize: Math.max(11, 15 - indent * 1.5), minWidth: 40, textAlign: 'right',
        flexShrink: 0, paddingLeft: 10 }}>{n}</span>
    </div>
  );
}

// ⚠️ SUPPRESSED BELOW A FLOOR. A percentage from three emails is noise that gets
// quoted as fact; counts alone are honest at any n.
// ⚠️ DOORLY BRAND COLOURS ONLY. This card had accumulated #2d6a0a, #b45309 and
// #c026d3 — a green, an amber and a magenta that appear nowhere in
// tailwind.config.js or index.css. Every value below is from the published
// palette, so the card cannot drift from the brand by someone reaching for a
// colour that "looks about right".
const BRAND = {
  blue: '#3a6ad9',      // doorly-blue — the primary, used for the neutral rungs
  teal: '#14B8A6',      // doorly-teal — a good outcome
  orange: '#FF7A1A',    // doorly-orange — needs attention, not an error
  gold: '#C9A84C',      // doorly-gold — withheld on purpose
  slate: '#64748B',     // doorly-slate — no signal either way
  error: '#ff082f',     // --color-error — a fault
  slateLight: '#E7EAF0',
};

const FUNNEL_MIN_N = 5;
// A distinct value, not '', so the row can tell "no percentage applies here"
// (the top rung, which is its own denominator) from "withheld on purpose".
const SUPPRESSED = Symbol('below the floor');
const pctOf = (num, den) => (den >= FUNNEL_MIN_N ? `${Math.round((num / den) * 100)}%` : SUPPRESSED);

// ─────────────────────────────────────────────────────────────────────────────
// WHY EVA DID NOT ATTEMPT — every category, in the reporter's words.
//
// ⚠️ THE FULL SET, NOT THE ONES CURRENTLY ON SCREEN. Two categories have ever
// appeared in production, so writing copy for those two would leave the other
// sixteen to surface unexplained on the day they first fire — which is the day
// someone is asking what they mean.
//
// Each says what the category IS and what TRIGGERS it, because "specific_file"
// answers neither on its own. Sources of truth:
//   backend/utils/inboundAllowlist.js — REFUSALS, checked before the model
//   backend/utils/inboundEva.js       — refusals from the attempt itself
// A category added there and not here still renders, with a fallback tooltip.
// ─────────────────────────────────────────────────────────────────────────────
const NO_ATTEMPT_COPY = {
  // ── refused before Eva was asked (utils/inboundAllowlist.js) ──
  has_attachment: 'The email carried an attachment. Sending a document is an action, not a question — '
    + 'answering the words while ignoring the file would read as though the file had been received and considered. '
    + 'Checked first, before anything else, even for a perfectly answerable question.',
  complaint: 'The message reads as a complaint — words like "unacceptable", "misled", "scam" or "disappointed". '
    + 'A complaint answered by a machine is the case this whole filter exists for.',
  adverse_decision: 'The sender is asking about a declined, denied or reconsidered application — "why was I", '
    + '"turned down", "appeal". The wording of an adverse action notice is regulated, and Eva has no access to '
    + 'why the decision was made, so she is never the first voice on one.',
  distress: 'The message mentions foreclosure, eviction, bankruptcy, illness, bereavement, divorce or not being '
    + 'able to afford something. There is no version of this a first-response bot should take.',
  legal_or_regulator: 'An attorney, lawsuit, subpoena or regulator is named — CFPB, attorney general, BBB, '
    + 'NMLS complaint, arbitration. This changes who should be replying, immediately.',
  suitability: 'The sender asked what they should do or whether they would qualify — "do I qualify", '
    + '"should I apply", "what is my best option". Answering that is advice, which Eva is not permitted to give '
    + 'even where she would guess correctly.',
  specific_file: 'The sender asked about their own application, which Eva has no access to — their loan, rate, '
    + 'closing, documents, or "where is my…". She could only guess, and a confident guess about someone\'s own '
    + 'file is the worst failure available here.',
  body_unavailable: 'The message body never reached us, so none of the checks above could run. Eva refuses rather '
    + 'than answering on a subject line alone — which is exactly what happened to the first real inbound email, '
    + 'with complaint and distress detection structurally unable to fire.',
  empty: 'Nothing to read — no subject and no body.',
  empty_body: 'A subject line and no body. "Need Help" is not a question.',
  refusal_check_failed: 'The safety check itself errored. It fails closed: when Eva cannot confirm it is safe to '
    + 'answer, she does not answer.',

  // ── Eva was asked, and the attempt did not produce a sendable answer ──
  model_declined: 'Eva was asked and said she could not answer it from published material — she returns '
    + 'CANNOT_ANSWER rather than guessing, and a refusal trailing an answer still counts as a refusal. '
    + 'This is the category to watch: unlike the ones above it is not a rule, it is Eva judging her own coverage.',
  model_error: 'The model call failed or timed out. A fault on our side, not a decision — the sender still got '
    + 'an acknowledgement and a human still received the ticket.',
  no_model_configured: 'No AI model is configured in this environment, so no attempt was possible.',
  no_bundle: 'The published-material bundle Eva answers from was unavailable, so she had nothing to answer from.',
  reg_z_gate: 'Eva\'s draft named a rate, payment or other figure that Regulation Z treats as a triggering term, '
    + 'which would oblige a full set of disclosures in the same message. The draft was withheld rather than sent.',
  reg_z_gate_unavailable: 'The Reg Z check on Eva\'s outgoing wording could not run, so the draft was withheld '
    + 'rather than sent unchecked.',
  unknown: 'No reason was recorded on the row. This should not happen; if it appears, the outcome was written by '
    + 'a path that did not name its refusal.',
};

const TABS = [
  // ⚠️ ALL IS A TAB, NOT AN ABSENCE. The three roles partition only the tickets
  // that HAVE an owner; without this the page's default view silently excludes
  // every undrived one.
  { key: '', label: 'All' },
  { key: 'client_success', label: 'Client Success' },
  { key: 'partner_success', label: 'Partner Success' },
  { key: 'engineering', label: 'Engineering' },
];

const card = { backgroundColor: '#fff', borderRadius: '12px', padding: '20px', border: '1px solid #f3f4f6', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' };

// Enums mirror backend/constants/escalationRouting.js.
// ⚠️ MOVED TO ../constants/ticketRoles. Re-exported so any existing importer
// keeps working, but nothing should import enums from a PAGE — see the note
// there. AdminTicketDetail used to, which coupled two routes for two arrays.
// ⚠️ IMPORTED *AND* RE-EXPORTED. `export { X } from '…'` is a pure re-export —
// it does NOT bind X in this module's scope, so the TICKET_ROLES.map() below
// would throw "TICKET_ROLES is not defined" at render. Exactly the shape of
// failure the root error boundary reports as "Something went wrong" with no
// indication of where.
export { TICKET_TOPICS, TICKET_ROLES };
// ⚠️ ONE LABELLER, AND IT TITLE-CASES. "Suggested: Client_success" reached the
// screen because that one call site printed the raw enum and leaned on CSS
// capitalize, which cannot see an underscore — so the fix belongs here rather
// than at the call site, or the next surface inherits the same hole.
const label = (s) => (s || '').replace(/_/g, ' ').replace(/\b[a-z]/g, (c) => c.toUpperCase());
const PRIORITY_COLOR = { high: '#ff082f', medium: '#d97706', low: '#6b7280' };

function ageText(h) {
  if (h == null) return '—';
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24); return `${d}d ${h % 24}h`;
}

const sel = { padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', background: '#fff', color: '#374151' };

export default function DashCustomerService() {
  const navigate = useNavigate();
  const token = localStorage.getItem('doorly_admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const { isAdmin } = useStaffRole();
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({ open: 0, needs_attention: 0, by_owner_role: {}, not_derived: 0, disagreements: 0, filtered: 0 });
  const [filtered, setFiltered] = useState([]);
  const [eva, setEva] = useState(null);
  // ⚠️ COLLAPSED BY DEFAULT. Not persisted: a sticky panel state leaves someone
  // looking at a card they opened weeks ago and forgot, which is the same
  // failure the test-accounts toggle avoids on the pipeline.
  // ⚠️ ONE RANGE FOR BOTH CARDS. Two independent pickers is two cards that can
  // disagree about what period they are showing, and nothing on screen would say
  // so. Default is all time — a card that silently windows itself reports a drop
  // that is really a filter.
  const [evaDays, setEvaDays] = useState('');
  const [evaOpen, setEvaOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [newTicket, setNewTicket] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactOpen, setContactOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [chat, setChat] = useState(null);
  const [declines, setDeclines] = useState(null);
  const [showFiltered, setShowFiltered] = useState(false);
  // ── (1) message detail + (4) bounded delete ────────────────────────────────
  const [selected, setSelected] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [msgDetail, setMsgDetail] = useState(null);
  const [msgLoading, setMsgLoading] = useState(false);
  // ⚠️ ONLY SOLICITATION ROWS ARE DELETABLE, and select-all uses this list so
  // the checkbox never promises an action the server will refuse.
  // ⚠️ TEST ROWS ARE DELETABLE NOW, and the bound is still a bound. Solicitation
  // is mail from strangers; a test row is mail WE sent ourselves. Both are "not
  // a real person waiting on us", and the server enforces exactly that set —
  // this list only has to agree with it, so the box never offers an action the
  // server will refuse. Anything that reached a TICKET stays undeletable: its
  // text is rendered on that ticket, and removing the source leaves a message
  // whose provenance no longer exists.
  const deletableIds = filtered.filter((m) => !m.ticket_id).map((m) => m.id);

  // ⚠️ THE EMAILS FIRST, THE ANALYSIS ON REQUEST. A model's reading of messages
  // nobody can see is a second opinion about a black box; the questions load
  // immediately and the analysis is a deliberate second click, over the same rows.
  // ⚠️ CHANGING WHO OR STANDING RESETS THE THINGS THAT DEPEND ON THEM. A topic
  // valid for an existing borrower is often meaningless for a prospective
  // partner, and a linked account is simply wrong once the answer to "who" has
  // changed — leaving either behind is how a ticket ends up filed against
  // somebody it has nothing to do with.
  const resetDependents = (patch) => setNewTicket((p) => {
    const next = { ...p, ...patch };
    const allowed = topicsFor(next.who, next.standing);
    return {
      ...next,
      // ⚠️ allowed[0], NOT 'general'. `general` routes to client_success whoever
      // the requester is, so defaulting a PARTNER ticket to it files it with the
      // borrower team. Each list opens on the right default for its audience.
      topic: allowed.includes(next.topic) ? next.topic : allowed[0],
      user_id: null, contact_label: '',
      external_name: '', external_email: '', external_phone: '',
    };
  });
  const pickWho = (who) => { setContacts([]); resetDependents({ who }); };
  const pickStanding = (standing) => { setContacts([]); resetDependents({ standing }); };

  // Debounced lookup for the existing-account picker. Empty query returns the
  // alphabetical head of the list, so opening the field shows something to
  // scroll rather than nothing until you type.
  useEffect(() => {
    if (!newTicket || newTicket.standing !== 'existing') return undefined;
    const q = newTicket.contact_label || '';
    const t = setTimeout(() => {
      axios.get(`${API}/api/admin/ticket-contacts?type=${newTicket.who}&q=${encodeURIComponent(q)}`, { headers })
        .then((r) => setContacts(r.data?.contacts || [])).catch(() => setContacts([]));
    }, 200);
    return () => clearTimeout(t);
  }, [newTicket?.who, newTicket?.standing, newTicket?.contact_label]); // eslint-disable-line react-hooks/exhaustive-deps

  const createManualTicket = async () => {
    setCreating(true);
    setNewTicket((p) => ({ ...p, error: null }));
    try {
      const r = await axios.post(`${API}/api/admin/tickets/manual`, {
        // ⚠️ `who` IS SENT, NOT INFERRED. It decides which table the id is
        // looked up in — partner_users and users have overlapping ids, so an id
        // alone cannot say which person it means.
        who: newTicket.who,
        user_id: newTicket.user_id || null,
        external_name: newTicket.external_name || null,
        external_email: newTicket.external_email || null,
        external_phone: newTicket.external_phone || null,
        topic: newTicket.topic, summary: newTicket.summary,
        channel: newTicket.channel, priority: newTicket.priority,
      }, { headers });
      setNewTicket(null);
      loadTickets();
      // Straight into the ticket — whoever logged it usually has more to add,
      // and a confirmation toast that then makes them go and find it is worse
      // than simply taking them there.
      navigate(`/admin/dashboard/customer-service/tickets/${r.data.ticket_number}`);
    } catch (e) {
      setNewTicket((p) => ({ ...p, error: e.response?.data?.error || 'Could not create the ticket.' }));
    }
    setCreating(false);
  };

  const openDeclines = async (reason) => {
    setDeclines({ reason, loading: true, messages: [], analysis: null });
    try {
      const r = await axios.get(`${API}/api/admin/inbound/no-attempt/${reason}`, { headers });
      setDeclines({ reason, loading: false, messages: r.data?.messages || [], analysis: null });
    } catch (_) {
      setDeclines({ reason, loading: false, messages: [], analysis: null, error: 'Could not load those messages.' });
    }
  };
  const analyseDeclines = async () => {
    setDeclines((d) => ({ ...d, analysing: true }));
    try {
      const r = await axios.post(`${API}/api/admin/inbound/kb-gaps`, { reason: declines.reason }, { headers });
      setDeclines((d) => ({ ...d, analysing: false, analysis: r.data?.analysis || 'Nothing to analyse.' }));
    } catch (e) {
      setDeclines((d) => ({ ...d, analysing: false, analysis: e.response?.data?.error || 'Analysis failed.' }));
    }
  };

  const openMessage = async (id) => {
    setMsgLoading(true); setMsgDetail({ id });
    try {
      const r = await axios.get(`${API}/api/admin/inbound/${id}`, { headers });
      setMsgDetail(r.data);
    } catch { setMsgDetail({ id, error: 'Could not load the message.' }); }
    setMsgLoading(false);
  };

  const deleteSelected = async () => {
    // ⚠️ CONFIRMED HERE *AND* SERVER-SIDE. The endpoint requires confirm:'DELETE'
    // — a destructive route whose only guard is a window.confirm is guarded by
    // the caller's good manners.
    if (!window.confirm(
      `Permanently delete ${selected.length} filtered message${selected.length === 1 ? '' : 's'}?\n\n`
      + 'This cannot be undone. Only messages classified as solicitation are removed — '
      + 'test-flagged rows are kept.'
    )) return;
    setDeleting(true);
    try {
      const r = await axios.post(`${API}/api/admin/inbound/delete`,
        { ids: selected, confirm: 'DELETE' }, { headers });
      const gone = new Set(selected);
      setFiltered((prev) => prev.filter((x) => !gone.has(x.id) || (r.data?.refused || []).includes(x.id)));
      setSelected([]);
      if (r.data?.refused?.length) {
        window.alert(`${r.data.deleted} deleted. ${r.data.refused.length} refused — those are not solicitation rows.`);
      }
    } catch (e) {
      window.alert(e.response?.data?.error || 'Could not delete.');
    }
    setDeleting(false);
  };
  const [moving, setMoving] = useState(null);
  const [roster, setRoster] = useState([]);
  const [stats, setStats] = useState({ messages: {}, surveys: {} });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  // Default view: open queue, whole team, oldest first.
  // ⚠️ DEFAULTS TO ALL, NOT TO A TAB. Opening on client_success showed 2 of 8
  // open tickets and hid the 3 with no derived owner entirely — they live behind
  // a badge, not a tab. To anyone who used this page before the tabs, that reads
  // as "the tickets disappeared", and they were right: a filter applied on load
  // is indistinguishable from missing data.
  const [f, setF] = useState({ status: 'open', assigned_role: '', assignee: '', topic: '', sort: 'age', dir: 'asc', owner_role: '' });

  const loadTickets = useCallback(async () => {
    const qs = Object.entries(f).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
    try {
      const r = await axios.get(`${API}/api/admin/tickets?${qs}`, { headers });
      setTickets(r.data.tickets || []);
      setCounts(r.data.counts || { open: 0, needs_attention: 0, by_owner_role: {} });
      setLoadError(null);
    } catch (e) {
      // ⚠️ A FAILED FETCH MUST NOT RENDER AS AN EMPTY QUEUE. This caught the
      // error, set an empty array and said nothing — so a 500 from a duplicate
      // SQL alias displayed as All (0), every tab (0), Open 0, with nine
      // tickets in the table. "Nothing to do" and "we could not ask" are
      // opposite facts and looked identical.
      setTickets([]);
      setLoadError(e?.response?.status ? `HTTP ${e.response.status}` : (e?.message || 'request failed'));
    }
    setLoading(false);
  }, [f, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadTickets(); }, [loadTickets]);
  useEffect(() => {
    axios.get(`${API}/api/admin/roster`, { headers }).then((r) => setRoster(r.data || [])).catch(() => {});
    axios.get(`${API}/api/admin/dashboard/customer-service-data`, { headers }).then((r) => setStats(r.data || { messages: {}, surveys: {} })).catch(() => {});
    axios.get(`${API}/api/admin/inbound/filtered`, { headers }).then((r) => setFiltered(r.data?.rows || [])).catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ⚠️ ITS OWN EFFECT, KEYED ON THE RANGE. Both Eva cards refetch when the range
  // changes; folded into the effect above they would have been fetched once on
  // mount and the picker would have moved nothing — a filter that silently does
  // not apply is worse than no filter, because the number looks answered.
  useEffect(() => {
    const qs = evaDays ? `?days=${evaDays}` : '';
    axios.get(`${API}/api/admin/eva-inbound-metrics${qs}`, { headers })
      .then((r) => setEva(r.data || null)).catch(() => {});
    axios.get(`${API}/api/admin/eva-chat-metrics${qs}`, { headers })
      .then((r) => setChat(r.data)).catch(() => setChat(null));
  }, [token, evaDays]); // eslint-disable-line react-hooks/exhaustive-deps

  // ⚠️ SUPPRESSED BELOW n=5. 100% of two ratings must never render like 100% of
  // two hundred, and a CSAT card is exactly the number people quote out of
  // context. Below the floor the card says what is missing, not a number.
  // ⚠️ TWO CSAT NUMBERS, NEVER ONE. "How did Eva do?" rates the assistant;
  // "how did we do?" is asked after a PERSON resolved the ticket and rates the
  // team. Averaged together they would answer neither — and the team half was
  // previously not collected at all, so every satisfaction figure we had was
  // about Eva while reading as though it were about support.
  // ⚠️ ONE ROLLED-UP CSAT, WITH THE SPLIT ON HOVER. Eva and the team are rated by
  // different people about different things, and that distinction still decides
  // what you DO about a low number — so it is kept, in the tooltip, rather than
  // dissolved into the headline. What the headline answers is the simpler
  // question a KPI row is read for: are the people we help satisfied.
  //
  // ⚠️ THE AVERAGE COMES FROM THE SERVER, over every scored row. Averaging the
  // two halves here would weight five team scores equally against a hundred of
  // Eva's, which is a different and wrong number.
  const evaCsat = eva?.csat?.eva || { n: 0, avg: null };
  const teamCsat = eva?.csat?.team || { n: 0, avg: null };
  const csatN = eva?.csat?.n || 0;
  const csatAvg = eva?.csat?.avg ?? null;
  const csatValue = csatN >= FUNNEL_MIN_N && csatAvg != null ? `${csatAvg.toFixed(1)} / 5` : null;
  const part = (lbl, c) => `${lbl}: ${c.avg != null ? `${c.avg.toFixed(1)} / 5` : '—'} (${c.n})`;
  const csatSplit = `Satisfaction across all support, from ${csatN} rating${csatN === 1 ? '' : 's'}. `
    + `${part('Eva', evaCsat)} · ${part('the team', teamCsat)}. `
    + `Eva is rated after she answers; the team after a person resolves a ticket. `
    + `Withheld below ${FUNNEL_MIN_N} ratings.`;
  // ⚠️ "None yet" READS AS AN ABSENCE; "NA" reads as an error or a field that
  // does not apply. Same length, and the distinction matters on a card whose
  // whole job is to say honestly that there is nothing yet.
  const csatEmpty = csatN === 0 ? 'None yet' : `Have ${csatN}, need ${FUNNEL_MIN_N}`;

  // ⚠️ THE SAME FLOOR, FOR THE SAME REASON. NPS published "100" off four
  // responses with no n beside it while CSAT hid at the same sample size — one
  // number on the row suppressed, its neighbour not. 100 is a TRUE NPS here
  // (four promoters, no detractors, (4−0)/4×100), not a mis-scaled 0–10
  // average, which is exactly why it is dangerous: it is arithmetically right
  // and quotable, and it is four people.
  // ⚠️ AND n TRAVELS WITH IT. A −100..+100 index and a 0–10 average are both
  // plausible readings of a bare number in a card, and the count is what tells
  // a reader how much weight it carries.
  const npsN = stats.surveys?.total || 0;
  const npsValue = npsN >= FUNNEL_MIN_N && stats.surveys?.nps != null
    ? `${stats.surveys.nps} (n=${npsN})`
    : null;
  const npsEmpty = npsN === 0 ? 'None yet' : `Have ${npsN}, need ${FUNNEL_MIN_N}`;

  // ── median time to first response ──────────────────────────────────────────
  // ⚠️ THE DENOMINATOR IS MEASURED TICKETS, NOT ALL OF THEM, and the same n=5
  // floor applies as everywhere else on this page. A median of one is a number,
  // not a measure.
  //
  // ⚠️ AND IT UNDER-REPORTS BY DESIGN — that is what the caption is for. One
  // channel sets first_response_at: a staff reply sent from the ticket itself,
  // and only once it has actually sent. A phone call, an SMS, an internal note,
  // or a reply from someone's own mailbox leaves the ticket unmeasured, and
  // unmeasured tickets are EXCLUDED rather than counted as slow. Saying
  // "covers N of M" is the only honest way to publish a number that cannot see
  // every channel.
  const ttfr = counts.ttfr || {};
  const ttfrValue = (ttfr.measured >= FUNNEL_MIN_N && ttfr.median_minutes != null)
    ? (ttfr.median_minutes < 60
      ? `${ttfr.median_minutes}m`
      : (ttfr.median_minutes < 1440
        ? `${Math.round(ttfr.median_minutes / 60)}h`
        : `${Math.round(ttfr.median_minutes / 1440)}d`))
    : null;
  const ttfrEmpty = !ttfr.measured ? 'None yet' : `Have ${ttfr.measured}, need ${FUNNEL_MIN_N}`;
  // ⚠️ AN EMPTY STATE WITH A REASON, so "None yet" reads as a fact about the
  // product rather than a broken card. Replying from a ticket now writes this,
  // so the empty state is no longer permanent — but until someone actually
  // replies it stays empty, and saying WHY is what stops it reading as a bug.
  // Eva is still deliberately not counted: she answers in about a second, and a
  // card reading "0m" would say the team responds instantly when the team has
  // not responded at all. Internal notes are excluded for the same reason —
  // updating a colleague is not answering the person waiting.
  const ttfrCaption = ttfr.unavailable
    ? 'Not yet available on this deploy.'
    : (!ttfr.measured
      ? 'No replies sent from a ticket yet. Eva’s automated answers and internal notes are excluded on purpose.'
      : `Replies sent from a ticket only, ${ttfr.measured} of ${ttfr.total || 0} tickets (90d). Phone, SMS and in-app replies are not recorded.`);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  // ⚠️ MOVE IS THE ONLY THING THAT CHANGES OWNERSHIP, and it goes through the
  // audited transfer endpoint — never a direct field edit. Reassignment (who is
  // working it) is a separate control and still cannot move the owner.
  const move = async (id, toRole) => {
    setMoving(id);
    try {
      await axios.post(`${API}/api/admin/tickets/${id}/transfer`, { to_role: toRole, reason: 'Moved from the hub' }, { headers });
      await loadTickets();
    } catch (e) { alert(e.response?.data?.error || 'Transfer failed.'); }
    setMoving(null);
  };

  const promote = async (id) => {
    try {
      const r = await axios.post(`${API}/api/admin/inbound/${id}/promote`, {}, { headers });
      setFiltered((prev) => prev.filter((x) => x.id !== id));
      await loadTickets();
      alert(`Ticket ${r.data?.ticket_number || ''} created.`);
    } catch (e) { alert(e.response?.data?.error || 'Could not create a ticket.'); }
  };

  // ⚠️ THE ROW STAYS ON SCREEN EITHER WAY. Unlike promote, this does not remove
  // it from the list — marking a message as a test hides it from a COUNT, not
  // from the people who need to see what was excluded.
  const markTest = async (id, on) => {
    try {
      await axios.post(`${API}/api/admin/inbound/${id}/test`, { is_test: on }, { headers });
      setFiltered((prev) => prev.map((x) => (x.id === id ? { ...x, is_test: on } : x)));
      const r = await axios.get(`${API}/api/admin/eva-inbound-metrics`, { headers });
      setEva(r.data || null);
    } catch (e) { alert(e.response?.data?.error || 'Could not update.'); }
  };

  // ── the funnel's rungs ──────────────────────────────────────────────────────
  // ⚠️ EVERY ONE READ FROM THE PAYLOAD, NONE DERIVED BY SUBTRACTION. The server
  // counts each state from the same rows and they sum to `received` by
  // construction. Computing any rung here as `received - answered` would put the
  // arithmetic back in the place it was wrong: a subtraction quietly absorbs
  // whatever nobody categorised and presents it as belonging to the rung it was
  // taken from.
  //
  // ⚠️ THE `?? eligible` FALLBACKS ARE FOR THE DEPLOY WINDOW ONLY. Backend and
  // frontend ship separately, so for a few minutes this card can receive the old
  // payload; without these it would render a funnel of zeroes and read as an
  // outage. They can go once the backend is out.
  const fn = eva?.funnel || {};
  const received = fn.received ?? fn.eligible ?? 0;
  const answered = fn.answered ?? fn.attempted ?? 0;
  const suppressed = fn.answered_suppressed ?? 0;
  const routed = fn.routed_to_human ?? 0;
  const replyFailed = fn.reply_failed ?? 0;
  const uncategorised = fn.uncategorised ?? 0;
  // Everything Eva produced an answer for, delivered or not.
  const answeredTotal = answered + suppressed;
  // ⚠️ ONE RUNG, BUT THE DISTINCTION STAYS RECOVERABLE. From the sender's side
  // nothing arrived in any of these cases, which is what the rung answers — but
  // what WE should do next differs, so the split is carried on the tooltip.
  // Only readable at all because eva_outcome is appended rather than
  // overwritten; before that, a failed send erased what preceded it.
  const stages = eva?.reply_failed_stages || {};
  const replyFailedDetail = [
    stages.after_answer ? `${stages.after_answer} after Eva had written an answer` : null,
    stages.after_refusal ? `${stages.after_refusal} after a refusal, so only the receipt was lost` : null,
    stages.before_decision ? `${stages.before_decision} before any decision was reached` : null,
  ].filter(Boolean).join('; ') || 'Stage not recorded.';

  return (
    <AdminLayout>
      <div style={{ padding: '28px 32px', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ marginBottom: '20px' }}>
          {/* ⚠️ THE LABEL TELLS THE TRUTH ABOUT THE SCOPE, because the scope is
              invisible otherwise. A non-admin sees only tickets they OWN — the
              server enforces it and no query string can widen it. A rep who has
              just been handed a partner and cannot see that partner's older
              resolved tickets will read it as a bug unless the heading says what
              the list contains. */}
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', marginBottom: '4px' }}>
            {isAdmin ? 'Tickets' : 'Tickets you own'}
          </h1>
          <p style={{ fontSize: '13px', color: '#6b7280' }}>
            {isAdmin
              ? 'Escalation tickets by owning team'
              : 'Tickets you are accountable for. Open tickets follow a partner when they are reassigned to you; resolved ones stay with whoever handled them.'}
          </p>
        </div>

        {/* ⚠️ THE FAILURE THE ZEROES HID. Every number below reads 0 when the
            request fails, which is indistinguishable from a quiet queue. Say so
            at the top, above the cards, before anyone reads a single count. */}
        {loadError && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#991b1b' }}>
            <strong>Could not load tickets ({loadError}).</strong> The counts and the list below are
            not showing your queue — they are showing nothing at all. Reload, and if it persists this
            is a server error, not an empty queue.
          </div>
        )}

        {/* Five cards, one row on a desk display; wraps rather than scrolls on narrow. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '28px' }}>
          {/* ⚠️ "Messages MTD" AND "Unread Messages" ARE GONE. They measured the
              in-app messages channel, which is not viewable from this page —
              its only presence here is a link OUT to /admin/messages. A headline
              metric describing a different surface is worse than a small number
              describing this one. "Survey Responses" went too: NPS already
              covers surveys and a raw count is not actionable. */}
          <KPI label="Open" value={loading ? '…' : counts.open} iconName="document" color="#3a6ad9" />
          {/* ⚠️ "Unowned" IS GONE, AND ONLY BECAUSE IT REACHED ZERO FIRST. Every
              open ticket now has a derived owner: the three owner_diagnostic
              test artifacts are closed, and T-2608-1021 was derived from its
              origin address. The signal itself is NOT lost — the "⚠️ N not
              derived" pill below the tabs renders whenever the count is
              non-zero, which is the right shape for an indicator: absent when
              there is nothing to indicate, impossible to miss when there is. */}
          {/* ⚠️ UNTOUCHED IS THE INTERIM, NOT THE ANSWER. It says whether the
              queue is being worked, not how fast we answer — a weaker question
              than time-to-first-response, and it ships because it is the one
              the existing timestamps can answer honestly today. */}
          <KPI label="Untouched" value={loading ? '…' : (counts.untouched ?? 0)} iconName="bell" color="#ff082f"
            caption="Open, with nothing recorded since creation." />
          <KPI label="Median first human response" value={ttfrValue || ttfrEmpty} muted={!ttfrValue}
            iconName="clock" color="#3a6ad9" caption={ttfrCaption} />
          <KPI label="Resolved this month" value={loading ? '…' : (counts.resolved_this_month ?? 0)} iconName="check-circle" color="#acf44e" />
          {/* ⚠️ SCOPE IS IN THE LABEL, because the KPI format has no room for a
              caption. These are two different measures of "how are we doing" on
              one row — NPS is journey milestones from survey responses, CSAT is
              per ticket across the whole handling including a human takeover.
              Without the qualifier one WILL be read as the other, so it travels
              in the label rather than as a caption a tidy-up can drop. */}
          <KPI label="Avg NPS (journey)" value={npsValue || npsEmpty} muted={!npsValue} iconName="star" color="#acf44e" />
          {/* ⚠️ ONE CARD, BOTH HALVES. The split lives on the tooltip — it is what
              decides what you do about a low score, so it is recoverable rather
              than discarded, the same way the reply-failure rung carries its own
              breakdown instead of losing it to the total. */}
          {/* caption=, not title=. The component already solved this: a native
              tooltip needs hover, and the split is exactly the kind of caveat a
              touch user must not lose. */}
          <KPI label="CSAT (Eva + team)" value={csatValue || csatEmpty} muted={!csatValue}
            iconName="check-circle" color="#3a6ad9" caption={csatSplit} />
        </div>

        {/* ── EVA'S PERFORMANCE ───────────────────────────────────────────────
            ⚠️ ONE CARD, TWO CHANNELS, ONE DATE FILTER. They were two cards with
            a picker each, which is two controls that can disagree about the
            period they show with nothing on screen saying so. The question is
            the same on both — does Eva resolve what people bring her — so the
            answer belongs in one place with one range.

            ⚠️ THE KPI ROWS ARE DELIBERATELY PARALLEL: volume, escalations, Eva
            answered, Eva resolved. A reader who has learned to read one row can
            read the other, and the two channels become comparable instead of
            each needing its own vocabulary.

            ⚠️ ONLY THE DETAIL TREES COLLAPSE. The KPI rows always show — the
            card exists to be glanced at, and a headline behind a disclosure is a
            headline nobody reads. */}
        <div style={{ ...card, marginBottom: 28 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
            {/* Same face as the Ticket Queue heading below — two card titles on
                one page reading as different kinds of thing is what makes a page
                feel assembled rather than designed. */}
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: 0 }}>Eva&rsquo;s Performance</h3>
            {/* ⚠️ ONE RANGE FOR BOTH CHANNELS. Applies to every number below it. */}
            <select value={evaDays} onChange={(e) => setEvaDays(e.target.value)}
              title="Applies to every number on this card"
              style={{ padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 8,
                fontSize: 12, fontWeight: 700, color: '#6b7280', background: '#fff' }}>
              <option value="">All time</option>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
            </select>
          </div>

          {/* ⚠️ SIDE BY SIDE, AND auto-fit SO IT STACKS ON ITS OWN. Two channels
              answering the same four questions belong beside each other — the
              comparison is the point, and stacked they were a scroll apart.
              minmax(320px, 1fr) means one column below ~700px rather than a
              breakpoint somebody has to remember to keep in step with the KPI
              grid inside it. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 28 }}>
          <div>
          {/* ── inbound email ── */}
          <div style={{ fontSize: 13.5, fontWeight: 800, color: '#374151', textTransform: 'uppercase',
            letterSpacing: '.04em', marginBottom: 12 }}>Inbound Email</div>
          {/* ⚠️ A 2×2 GRID, IDENTICAL IN BOTH SECTIONS. As a wrapping flex row the
              four KPIs broke at different points in each column — the widths
              differ with the numbers in them — so the two sections never lined
              up. A grid puts every KPI in the same place in both.

              ⚠️ AND NEVER WRITE A CLOSING COMMENT DELIMITER INSIDE A COMMENT.
              This block previously spelled one out while explaining that a JSX
              comment cannot sit in expression position — and the spelled-out
              delimiter ENDED THE COMMENT THERE, so the remaining two lines
              rendered as visible text under the Inbound Email heading. The
              hazard being described bit the description of it. */}
          {eva && received > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '14px 18px', marginBottom: 12 }}>
              {[
                { k: 'Emails', v: String(received), tone: '#111827',
                  t: 'Mail a person actually sent us. Spam, automated senders, solicitation and our own test messages are already out, so this is not everything that arrived.' },
                // ⚠️ ESCALATIONS SITS SECOND, BEFORE THE EVA NUMBERS. It is the
                // volume that reached a person, which is the figure staffing is
                // decided on — and reading it after the resolution rate invites
                // it to be read as Eva's failure rather than as the workload.
                { k: 'Escalations', v: String(routed),
                  sub: pctOf(routed, received) === SUPPRESSED ? `from ${FUNNEL_MIN_N}` : pctOf(routed, received),
                  tone: BRAND.orange,
                  t: 'Mail Eva was not allowed to answer, or could not, so a person picked it up. Most are categories she must never attempt — a complaint, an adverse decision, a question about someone\'s own file.' },
                { k: 'Eva Answered', v: String(answeredTotal),
                  sub: pctOf(answeredTotal, received) === SUPPRESSED ? `from ${FUNNEL_MIN_N}` : pctOf(answeredTotal, received),
                  tone: BRAND.blue, t: 'Eva produced an answer, delivered or not. The rest went to a person.' },
                { k: 'Eva Resolved', v: pctOf(eva.funnel.confirmed, answeredTotal) === SUPPRESSED ? '—' : pctOf(eva.funnel.confirmed, answeredTotal),
                  sub: pctOf(eva.funnel.confirmed, answeredTotal) === SUPPRESSED ? `percentages from ${FUNNEL_MIN_N}` : `${eva.funnel.confirmed} of ${answeredTotal} confirmed`,
                  tone: BRAND.teal,
                  t: `Senders who told us Eva's answer resolved it, out of everything she answered. Only they can say so — this is not "we heard nothing back, so it worked". Withheld below ${FUNNEL_MIN_N} answers.` },
              ].map((m) => (
                <div key={m.k} title={m.t} style={{ cursor: 'help' }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.05em' }}>{m.k}</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: m.tone, lineHeight: 1.2 }}>{m.v}</div>
                  {m.sub && <div style={{ fontSize: 11, color: '#9ca3af' }}>{m.sub}</div>}
                </div>
              ))}
            </div>
          )}
          {/* ⚠️ ONLY THE DETAIL TREE COLLAPSES. The KPI row above always shows —
              a headline behind a disclosure is a headline nobody reads. */}
          <button onClick={() => setEvaOpen((v) => !v)}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              color: BRAND.blue, fontSize: 11.5, fontWeight: 700, marginTop: 4 }}>
            {evaOpen ? 'Hide the detail' : 'Show the detail'}
          </button>
          {evaOpen && (!eva || received === 0 ? (
            <div style={{ color: '#9ca3af', fontSize: 13, padding: '10px 0' }}>No inbound yet.</div>
          ) : (
            <div style={{ fontSize: 13 }}>
              {/* ⚠️ "RECEIVED FROM A PERSON", NOT "ELIGIBLE INBOUND". "Eligible"
                  reads as eligible-FOR-EVA, which is not what it counts — it
                  counts what got past spam, automated senders and solicitation.
                  A top row named for a qualification invites the reader to treat
                  the rung below it as a pass rate. This one says what it counts.

                  It is also not plain "Received": roughly 95% of hello@ is cold
                  outreach, and counting that here would put coverage near 2% and
                  make correct filtering read as failure. */}
              <FunnelRow indent={0} label="Received from a person" n={received}
                info={eva.excluded_test > 0
                  ? `${eva.excluded_test} test message${eva.excluded_test === 1 ? '' : 's'} sent by us `
                    + `${eva.excluded_test === 1 ? 'is' : 'are'} excluded from every number on this card. `
                    + 'Marked, not deleted — readable under "Filtered".'
                  : null} />
              {/* ⚠️ "Eva answered" NOW MEANS "Eva PRODUCED an answer" — sent or
                  not — so the suppressed rung can sit under it as a sibling of
                  the three outcomes. The arithmetic still partitions: the first
                  three children are the answers that REACHED someone and sum to
                  `answered`; the fourth is the answers that did not, and the
                  four together are this parent. Nothing is double-counted and
                  nothing is subtracted.

                  ⚠️ AND THE PERCENTAGES ARE OF THE PARENT, INCLUDING SUPPRESSED.
                  A resolution rate computed only over delivered answers flatters
                  itself by excluding the ones nobody received. */}
              <FunnelRow indent={1} label="Eva answered" n={answeredTotal}
                pct={pctOf(answeredTotal, received)} tone={BRAND.blue}
                info={'Eva produced an answer. The rows beneath split it by what happened next — '
                  + 'including the answers that were written and never sent.'} />
              <FunnelRow indent={2} label="Confirmed resolved" n={eva.funnel.confirmed}
                pct={pctOf(eva.funnel.confirmed, answeredTotal)} tone={BRAND.teal} />
              <FunnelRow indent={2} label="Still needed help" n={eva.funnel.still_needed_help}
                pct={pctOf(eva.funnel.still_needed_help, answeredTotal)} tone={BRAND.orange} />
              {/* ⚠️ SHOWN, NEVER FOLDED AWAY. If this is 70% the resolution
                  number means very little, and that is the fact worth surfacing. */}
              <FunnelRow indent={2} label="No reply from the sender" n={eva.funnel.unknown}
                pct={pctOf(eva.funnel.unknown, answeredTotal)} tone={BRAND.slate} />
              <FunnelRow indent={2} label="Answered, but the reply was suppressed"
                n={suppressed} pct={pctOf(suppressed, answeredTotal)} tone={BRAND.gold}
                info={'Eva wrote an answer and we stopped it going out, so the sender received NOTHING. '
                  + 'The loop guards do this: the message looked automated, the thread had already had '
                  + 'five automated replies in fifteen minutes, or the reply was identical to the last '
                  + 'one — which means a loop is running. Replying into a loop is worse than a missing '
                  + 'reply, but a real person caught by it hears silence, so this number is worth '
                  + 'watching. The text Eva wrote is kept on the ticket and can be sent by hand.'} />
              {/* ⚠️ A REAL COUNT OF no_attempt ROWS — never received − answered.
                  A subtraction absorbs whatever nobody categorised and reports
                  it as though it belonged here, which is precisely how the two
                  rows above stayed invisible. */}
              <FunnelRow indent={1} label="Routed to a human" n={routed}
                pct={pctOf(routed, received)}
                info={'Eva was not allowed to answer, or could not, so the ticket went to a person. '
                  + 'The rows beneath say why — they are categories she must NEVER attempt, not a '
                  + 'target to move.'} />
              {/* ⚠️ THE REASONS ARE CHILDREN OF THE RUNG THEY SUM TO. Indentation
                  is the denominator everywhere else on this card, and these add
                  up to "Routed to a human" exactly — they are folded from the
                  same rows, so the arithmetic is guaranteed rather than hoped
                  for. Behind a toggle they were an unrelated-looking list. */}
              {(eva.no_attempt || []).map((r) => {
                const key = String(r.reason).split(':')[0];
                return (
                  <div key={r.reason} onClick={() => openDeclines(key)} style={{ cursor: 'pointer' }}>
                    <FunnelRow indent={2} n={r.n}
                      pct={pctOf(r.n, routed)} tone={BRAND.slate}
                      label={String(r.reason).replace(/_/g, ' ')}
                      info={NO_ATTEMPT_COPY[key] || 'No description recorded for this category.'} />
                  </div>
                );
              })}
              {/* ⚠️ model_declined IS THE ONLY ONE OF THESE WORTH MOVING, so it
                  gets the invitation. The others are rules working as designed —
                  a complaint, an adverse decision, a question about someone's own
                  file — and "improving" those numbers means weakening a guard.
                  This one is Eva judging her own coverage: she was allowed to
                  answer and could not source one, so every row behind it is a
                  question our published material does not answer. */}
              {(eva.no_attempt || []).some((r) => String(r.reason).startsWith('model_declined')) && (
                <div style={{ paddingLeft: 44, marginTop: 2 }}>
                  <button onClick={() => openDeclines('model_declined')}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                      color: BRAND.blue, fontSize: 11.5, fontWeight: 700 }}>
                    Read these and analyse the knowledge gaps →
                  </button>
                </div>
              )}
              {/* Error states appear only when they happen: a permanent zero row
                  for a fault trains the eye to skip the place faults appear. */}
              {replyFailed > 0 && (
                <FunnelRow indent={1} label="Reply failed to send" n={replyFailed}
                  pct={pctOf(replyFailed, received)} tone={BRAND.error}
                  info={'The send threw, so the sender received nothing. A fault, not a decision — '
                    + 'unlike suppression above.\n\n' + replyFailedDetail
                    + '\n\nAn undelivered ANSWER is worth resending: Eva wrote something useful and it '
                    + 'was lost, and nobody will notice on the sender\'s behalf. An undelivered RECEIPT '
                    + 'matters less — the ticket exists and a human replies either way.'} />
              )}
              {uncategorised > 0 && (
                <FunnelRow indent={1} label="Not categorised" n={uncategorised}
                  pct={pctOf(uncategorised, received)} tone={BRAND.error}
                  info={'These rows hold an eva_outcome the card does not recognise, so nobody can say what '
                    + 'happened to the sender: ' + ((eva.uncategorised_values || [])
                      .map((u) => `${u.outcome === null ? '(none recorded)' : u.outcome} ×${u.n}`).join(', ')
                      || 'value not reported')
                    + '. Shown rather than absorbed into a neighbouring rung — an uncategorised outcome is a '
                    + 'gap in the reporting, not a quantity belonging to whichever row it was nearest.'} />
              )}
            </div>
          ))}

          </div>

          <div>
          {/* ── chat ── */}
          <div style={{ fontSize: 13.5, fontWeight: 800, color: '#374151', textTransform: 'uppercase',
            letterSpacing: '.04em', marginBottom: 12 }}>Chat</div>
          {!chat || (chat.funnel?.sessions || 0) === 0 ? (
            <div style={{ color: '#9ca3af', fontSize: 13, padding: '10px 0' }}>No chat sessions yet.</div>
          ) : (() => {
            const c = chat.funnel;
            const asked = c.asked || 0;
            // Read from the payload, never sessions − escalated: a subtraction
            // absorbs whatever nobody categorised into whichever rung it is
            // taken from, which is how two outcome shapes went missing on the
            // inbound card.
            const handled = c.handled ?? 0;
            return (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '14px 18px', marginBottom: 12 }}>
                  {[
                    { k: 'Conversations', v: String(c.sessions), tone: '#111827',
                      t: 'Chat sessions where someone said something and Eva answered. Borrower, partner and prospect chat rolled up. Our own E2E traffic is excluded.' },
                    { k: 'Escalations', v: String(c.escalated),
                      sub: pctOf(c.escalated, c.sessions) === SUPPRESSED ? `from ${FUNNEL_MIN_N}` : pctOf(c.escalated, c.sessions),
                      tone: BRAND.orange,
                      t: 'Conversations where a ticket exists or the person asked for a human. Together with Eva Answered this accounts for every conversation.' },
                    // ⚠️ "HANDLED", NOT "RESOLVED", AND THE DIFFERENCE IS REAL.
                    // Every one of these got an answer and never needed a person
                    // — the operational number worth a headline. It is not a
                    // claim the answer was GOOD: someone who asked once, was
                    // answered badly and gave up lands here too. Resolution
                    // beside it is the claim only the person themselves can make.
                    { k: 'Eva Answered', v: String(handled),
                      sub: pctOf(handled, c.sessions) === SUPPRESSED ? `from ${FUNNEL_MIN_N}` : pctOf(handled, c.sessions),
                      tone: BRAND.blue,
                      t: 'Conversations Eva answered that never reached a person. Answered is not the same as resolved: someone who asked once, got a poor answer and gave up counts here too, which is why Eva Resolved sits beside it on a smaller denominator.' },
                    { k: 'Eva Resolved', v: pctOf(c.confirmed, asked) === SUPPRESSED ? '—' : pctOf(c.confirmed, asked),
                      sub: pctOf(c.confirmed, asked) === SUPPRESSED ? `percentages from ${FUNNEL_MIN_N}` : `${c.confirmed} of ${asked} asked`,
                      tone: BRAND.teal,
                      t: `Of the conversations Eva ASKED about, those the person told us she resolved. A smaller denominator than the others on purpose — she asks once per conversation and only after a substantive answer, so most conversations never reach the question. Withheld below ${FUNNEL_MIN_N}.` },
                  ].map((m) => (
                    <div key={m.k} title={m.t} style={{ cursor: 'help' }}>
                      <div style={{ fontSize: 10, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.05em' }}>{m.k}</div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: m.tone, lineHeight: 1.2 }}>{m.v}</div>
                      {m.sub && <div style={{ fontSize: 11, color: '#9ca3af' }}>{m.sub}</div>}
                    </div>
                  ))}
                </div>
                <button onClick={() => setChatOpen((v) => !v)}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    color: BRAND.blue, fontSize: 11.5, fontWeight: 700, marginTop: 4, marginBottom: 4 }}>
                  {chatOpen ? 'Hide the detail' : 'Show the detail'}
                </button>
                {chatOpen && (
                  <div style={{ fontSize: 13 }}>
                    <FunnelRow indent={0} label="Conversations" n={c.sessions} />
                    {/* ⚠️ TWO RUNGS THAT PARTITION IT: handled + escalated =
                        conversations, both real counts. This is the split the
                        card is actually read for — did it need a person — and it
                        sat behind a denominator nobody could parse before. */}
                    <FunnelRow indent={1} label="Handled without a human" n={handled}
                      pct={pctOf(handled, c.sessions)} tone={BRAND.blue}
                      info={'Eva answered and it never reached a person. Handled is not the same as resolved: '
                        + 'somebody who asked once, got a poor answer and gave up is counted here too. The rows '
                        + 'beneath are the ones who told us either way.'} />
                    {/* ⚠️ THE ASK IS A SMALLER DENOMINATOR, AND THE GAP IS THE POINT.
                        Eva asks once per conversation and only after a substantive
                        answer, so the sessions above this rung are people who never
                        got that far. Counting them as unresolved would report the
                        widget's open rate as Eva's failure. */}
                    <FunnelRow indent={2} label="Eva asked if it resolved things" n={asked}
                      pct={pctOf(asked, c.sessions)} tone={BRAND.blue}
                      info={'Asked once per conversation, after a substantive answer, and never after a turn '
                        + 'that opened a ticket — "did that answer your question?" straight after handing the '
                        + 'work to a person is a question about us, not about Eva. Sessions above this rung '
                        + 'never got that far.'} />
                    <FunnelRow indent={3} label="Confirmed resolved" n={c.confirmed}
                      pct={pctOf(c.confirmed, asked)} tone={BRAND.teal} />
                    <FunnelRow indent={3} label="Still needed help" n={c.still_needed_help}
                      pct={pctOf(c.still_needed_help, asked)} tone={BRAND.orange}
                      info={'They told us it did not resolve it, and were offered a human in the same breath. '
                        + 'This number going UP is not a failure — it is people telling us the truth where '
                        + 'previously they simply left.'} />
                    <FunnelRow indent={3} label="No answer to the question" n={c.no_answer}
                      pct={pctOf(c.no_answer, asked)} tone={BRAND.slate}
                      info={'Eva asked and they did not answer either way. A real count of sessions, never '
                        + 'asked minus answered — a subtraction would absorb anything nobody categorised.'} />
                    {/* The other half of the partition. Measured against all
                        conversations, because escalating usually happens before
                        Eva would have asked anything. */}
                    <FunnelRow indent={1} label="Escalated to a human" n={c.escalated}
                      pct={pctOf(c.escalated, c.sessions)} tone={BRAND.orange}
                      info={'A ticket exists for this conversation, or the person asked for a human. '
                        + 'Together with the rung above it accounts for every conversation.'} />
                  </div>
                )}
              </>
            );
          })()}
          </div>
          </div>
        </div>


        {/* ── Tabs: the three owning teams ──────────────────────────────
            ⚠️ THE TAB IS owner_role, NOT assigned_role. Who is ACCOUNTABLE, not
            who happens to be working it — a ticket on someone's desk still sits
            in its owner's tab. */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          {TABS.map((t) => {
            const active = f.owner_role === t.key;
            const n = t.key === '' ? (counts.open || 0) : (counts.by_owner_role?.[t.key] || 0);
            return (
              <button key={t.key} onClick={() => { setShowFiltered(false); setF((p) => ({ ...p, owner_role: t.key })); }}
                style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid ' + (active ? '#3a6ad9' : '#e5e7eb'),
                  background: active ? '#eef2fb' : '#fff', color: active ? '#3a6ad9' : '#374151',
                  fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                {t.label} <span style={{ opacity: 0.7 }}>({n})</span>
              </button>
            );
          })}

          {/* ⚠️ A COUNT BADGE, NOT A FOURTH TAB. owner_role IS NULL means nobody
              derived an owner — a gap in the model, not a desk that works a
              queue. Giving it a tab would file it away as normal; leaving it out
              entirely would hide it behind three tidy tabs. It is clickable so
              the tickets are reachable, and coloured as a warning because it is
              one. */}
          {counts.not_derived > 0 && (
            <button onClick={() => { setShowFiltered(false); setF((p) => ({ ...p, owner_role: 'not_derived' })); }}
              title="These tickets have no derived owner. That is a defect to fix, not a queue to work."
              style={{ padding: '8px 14px', borderRadius: 8, border: '1px dashed #c026d3',
                background: f.owner_role === 'not_derived' ? '#fdf4ff' : '#fff', color: '#c026d3',
                fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              ⚠️ {counts.not_derived} not derived
            </button>
          )}

          {/* ⚠️ NOT A TAB EITHER — there is no ticket and no owner. */}
          {/* ⚠️ THE LABEL NAMES WHAT THE CLICK DOES, NOT WHERE YOU ARE. It read
              "Filtered (n)" in both states, so once you were in the filtered view
              the button described the view you were already looking at and gave
              no hint that it was also the way out — the same control, apparently
              inert. It now reads "Unfiltered" while filtered, so the pair says
              go-there / come-back.

              ⚠️ THE COUNT ONLY BELONGS ON THE WAY IN. "Unfiltered (7)" would
              read as seven unfiltered things; the number describes what is
              behind the door, so it goes only on the label that opens it. */}
          {/* ⚠️ THE CHANNEL WITH NO ROW, sitting with the other queue controls
              rather than out on its own. Everything else in this hub arrived
              through something that creates its own ticket; a borrower who
              PHONES, or emails someone at Doorly directly, produced nothing at
              all — no owner, no first-response clock, no CSAT, and no presence
              in any number on this page.

              ⚠️ who AND standing ARE SEEDED HERE. Without them the modal opens
              with neither toggle selected and topicsFor() falls back — the form
              worked but looked like nothing had been chosen. An earlier edit
              missed this line by two spaces of indentation and changed nothing,
              silently, which is why the assertion above it now exists. */}
          <button onClick={() => setNewTicket({ channel: 'phone', topic: 'general', priority: 'medium', who: null, standing: null })}
            style={{ marginLeft: 'auto', padding: '8px 14px', borderRadius: 8, border: 'none',
              background: BRAND.blue, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            + Create ticket
          </button>
          <button onClick={() => setShowFiltered((v) => !v)}
            aria-pressed={showFiltered}
            title={showFiltered ? 'Back to the ticket queue' : 'Show messages filed as solicitation'}
            style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e5e7eb',
              background: showFiltered ? '#f3f4f6' : '#fff', color: '#6b7280', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            {showFiltered ? 'Unfiltered' : `Filtered (${counts.filtered || 0})`}
          </button>
        </div>

        {/* ── The filtered list: cold outreach, filed, never ticketed ────────
            ⚠️ FILED IS NOT DELETED. At ~95% of inbound volume a misclassified
            partner enquiry that vanished is a lost partner, so every row stays
            and there is somewhere to look. */}
        {showFiltered && (
          <div style={{ ...card, marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 10, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>Filtered as solicitation</div>
              {/* ⚠️ DELETE IS BOUNDED TO SOLICITATION ROWS, AND THE SERVER
                  ENFORCES THAT — the WHERE clause carries it, so a test-flagged
                  row cannot be removed here however it got selected. Test rows
                  share this list but not the reasoning: a solicitation is junk,
                  a test row is our own record of a real message, marked rather
                  than deleted on purpose. */}
              {selected.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>{selected.length} selected</span>
                  <button onClick={deleteSelected} disabled={deleting}
                    style={{ padding: '5px 10px', borderRadius: 8, border: '1px solid #ffc5ca',
                      background: '#fff0f3', color: '#ff082f', fontSize: 12, fontWeight: 700,
                      cursor: deleting ? 'wait' : 'pointer' }}>
                    {deleting ? 'Deleting…' : 'Delete selected'}
                  </button>
                </div>
              )}
            </div>
            {filtered.length === 0 ? (
              <div style={{ color: '#9ca3af', fontSize: 13, padding: '18px 0', textAlign: 'center' }}>
                Nothing filtered yet.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ textAlign: 'left', color: '#6b7280' }}>
                  <th style={{ padding: 8, width: 28 }}>
                    {/* Select-all covers only the DELETABLE rows, so the box
                        cannot promise an action the server will refuse. */}
                    <input type="checkbox"
                      checked={deletableIds.length > 0 && selected.length === deletableIds.length}
                      onChange={(e) => setSelected(e.target.checked ? deletableIds : [])} />
                  </th>
                  <th style={{ padding: 8 }}>From</th><th style={{ padding: 8 }}>Subject</th>
                  <th style={{ padding: 8 }}>Received</th><th style={{ padding: 8 }} />
                </tr></thead>
                <tbody>
                  {filtered.map((m) => (
                    /* ⚠️ THE WHOLE ROW OPENS THE MESSAGE, not just the From cell.
                       A click target that is one column wide is a target you have
                       to find; the row is what a reader is already pointing at.
                       Matches the ticket queue above, which has always been
                       row-clickable. */
                    <tr key={m.id}
                      onClick={() => openMessage(m.id)}
                      style={{ borderTop: '1px solid #f3f4f6', cursor: 'pointer' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#f9fafb'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                      {/* ⚠️ THE CONTROLS STOP PROPAGATION. Selecting a checkbox or
                          hitting an action button must not also open the message —
                          a row-level handler that swallows its own children's
                          clicks is how "mark as test" starts opening a modal. */}
                      <td style={{ padding: 8 }} onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox"
                          disabled={!!m.ticket_id}
                          title={m.ticket_id
                            ? 'This reached a ticket — its text is shown on that ticket, so the source is kept'
                            : undefined}
                          checked={selected.includes(m.id)}
                          onChange={(e) => setSelected((p) => (e.target.checked ? [...p, m.id] : p.filter((x) => x !== m.id)))} />
                      </td>
                      <td style={{ padding: 8, color: '#111827' }}>
                        {m.from_name || m.from_email}
                        {m.is_test && (
                          <span title="Excluded from the Eva funnel. The message is kept, not deleted."
                            style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: '#6b7280',
                              background: '#f3f4f6', padding: '1px 6px', borderRadius: 9999 }}>TEST</span>
                        )}
                      </td>
                      <td style={{ padding: 8, color: '#374151' }}>{m.subject || '(no subject)'}</td>
                      <td style={{ padding: 8, color: '#6b7280' }}>{m.received_at ? new Date(m.received_at).toLocaleDateString() : '—'}</td>
                      <td style={{ padding: 8, textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                        {/* ⚠️ REVERSIBLE, AND THAT IS NOT A CONVENIENCE. Marking a
                            real customer as a test would hide them from the only
                            view that would catch the mistake. */}
                        <button onClick={() => markTest(m.id, !m.is_test)}
                          title={m.is_test ? 'Count this message again' : 'One of ours — exclude from the funnel, keep the record'}
                          style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #d1d5db', marginRight: 6,
                            background: '#fff', color: '#6b7280', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                          {m.is_test ? 'Not a test' : 'Mark as test'}
                        </button>
                        {/* ⚠️ THE RESCUE AND THE CORRECTION SIGNAL ARE ONE ACTION.
                            Asking for both separately means the second never happens. */}
                        <button onClick={() => promote(m.id)}
                          style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #3a6ad9',
                            background: '#fff', color: '#3a6ad9', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                          This is real → create ticket
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ── Ticket queue ── */}
        <div style={card}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: 0, marginRight: 'auto' }}>Ticket Queue</h3>
              {/* Three states, not four. "Assigned" is an assignment fact, not a
                  lifecycle one — the Any-assignee filter beside this already answers
                  it, so having both made two controls overlap. Default stays Open. */}
              <select style={sel} value={f.status} onChange={set('status')}>
                <option value="open">Open</option>
                <option value="">All</option>
                <option value="resolved">Resolved</option>
              </select>
            <select style={sel} value={f.assignee} onChange={set('assignee')}>
              {/* ⚠️ "Mine" was ambiguous once ownership and assignment split.
                  Two filters, because one toggle cannot express "I own it but
                  someone else is working it". */}
              <option value="">Any assignee</option>
              <option value="i_own">I own</option>
              <option value="im_working">I'm working</option>
              <option value="unowned">Unowned (no accountable person)</option>
              <option value="unassigned">Unassigned (nobody working)</option>
              {roster.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <select style={sel} value={f.assigned_role} onChange={set('assigned_role')}>
              <option value="">Any role</option>
              {TICKET_ROLES.map((r) => <option key={r} value={r}>{label(r)}</option>)}
            </select>
            <select style={sel} value={f.topic} onChange={set('topic')}>
              <option value="">Any topic</option>
              {TICKET_TOPICS.map((t) => <option key={t} value={t}>{label(t)}</option>)}
            </select>
            <select style={sel} value={`${f.sort}:${f.dir}`} onChange={(e) => { const [sort, dir] = e.target.value.split(':'); setF((p) => ({ ...p, sort, dir })); }}>
              <option value="age:asc">Oldest first</option>
              <option value="age:desc">Newest first</option>
              <option value="priority:desc">Priority (high→low)</option>
            </select>
          </div>

          {loading ? (
            <div style={{ color: '#9ca3af', fontSize: '13px', textAlign: 'center', padding: '32px 0' }}>Loading…</div>
          ) : tickets.length === 0 ? (
            <div style={{ color: '#9ca3af', fontSize: '13px', textAlign: 'center', padding: '32px 0' }}>No tickets match this view.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#6b7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    <th style={{ padding: '8px 10px' }}>Ref</th>
                    <th style={{ padding: '8px 10px' }}>Topic</th>
                    <th style={{ padding: '8px 10px' }}>Requester</th>
                    <th style={{ padding: '8px 10px' }}>Role</th>
                    {/* ⚠️ TWO COLUMNS, TWO MEANINGS. Owner = accountable, derived
                        once at creation and never moved by a reassign. Assignee =
                        who is working it now. One column carrying both is the
                        ambiguity this split removes. */}
                    <th style={{ padding: '8px 10px' }}>Owner</th>
                    <th style={{ padding: '8px 10px' }}>Assignee</th>
                    <th style={{ padding: '8px 10px' }}>Status</th>
                    <th style={{ padding: '8px 10px' }}>Priority</th>
                    <th style={{ padding: '8px 10px' }}>Age</th>
                    <th style={{ padding: '8px 10px' }}>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((t) => (
                    <tr key={t.id}
                      onClick={() => navigate(`/admin/dashboard/customer-service/tickets/${t.ticket_number}`)}
                      style={{ borderTop: '1px solid #f3f4f6', cursor: 'pointer', background: t.aging ? '#fff7f8' : '#fff' }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                      onMouseLeave={(e) => e.currentTarget.style.background = t.aging ? '#fff7f8' : '#fff'}>
                      <td style={{ padding: '10px', fontWeight: 700, color: '#3a6ad9', whiteSpace: 'nowrap' }}>
                        {t.ticket_number}
                        {/* ⚠️ "NEW" MEANS SINCE A HUMAN LAST SAID SOMETHING, not
                            since the ticket was touched — updated_at moves on a
                            priority change, which would clear this without
                            anyone having read the message. Computed server-side
                            against the latest staff reply or note. */}
                        {/* ⚠️ REOPENED SITS BESIDE THE NEW BADGE, NOT INSTEAD OF
                            IT. They answer different questions: NEW is "someone
                            wrote since we last spoke", REOPENED is "we had
                            closed this and they came back". A ticket can be
                            both, and the second is the one that says a judgement
                            of ours was wrong. */}
                        {t.reopened_at && (
                          <span title="Resolved, then the requester replied — reopened automatically."
                            style={{ marginRight: 6, fontSize: 10, fontWeight: 800, color: '#b45309', background: '#fef3c7', padding: '2px 6px', borderRadius: 9999 }}>
                            REOPENED
                          </span>
                        )}
                        {t.unread_inbound > 0 && (
                          <span title={`${t.unread_inbound} message${t.unread_inbound === 1 ? '' : 's'} since anyone replied`}
                            style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: '#fff',
                              background: '#ff082f', padding: '1px 6px', borderRadius: 9999 }}>
                            {t.unread_inbound} NEW
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '10px', textTransform: 'capitalize' }}>{label(t.topic)}</td>
                      {/* ⚠️ user_id is NULL on external tickets BY DESIGN, which rendered
                          as "#null". Show the submitted name and mark it unverified — a
                          public form proves nothing about who sent it. */}
                      <td style={{ padding: '10px' }}>
                        {/* ⚠️ THE ADDRESS IS SHOWN AS AN ADDRESS. An inbound
                            ticket from someone who never gave a name used to
                            render "Name not given", which is unusable — the
                            address is the only identifier there is, and the one
                            a person searches for. Rendered in mono and dimmed so
                            nobody reads it as a person's name. */}
                        {t.requester_is_email && t.requester_name
                          ? <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, color: '#6b7280' }}>{t.requester_name}</span>
                          : (t.requester_name || 'Name not given')}
                        {t.requester_verified === false && (
                          <span title="Submitted through a public form — unverified."
                                style={{ marginLeft: 6, fontSize: 9, fontWeight: 800, color: '#b45309', background: '#fef3c7', padding: '1px 5px', borderRadius: 9999 }}>
                            UNVERIFIED
                          </span>
                        )}
                        {/* 🔑 FILED ON BEHALF (push C). */}
                        {t.on_behalf && (
                          <span title={t.submitted_by_line || 'Filed by staff on the requester\u2019s behalf'}
                                data-testid="ticket-on-behalf-tag"
                                style={{ marginLeft: 6, fontSize: 9, fontWeight: 800, color: '#1e40af', background: '#eff6ff', padding: '1px 5px', borderRadius: 9999 }}>
                            ON BEHALF
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '10px', textTransform: 'capitalize', color: '#6b7280' }}>
                        {label(t.assigned_role)}
                        {/* STEP 4: the topic map is a SUGGESTION now. Displayed,
                            never acted on — nothing reads suggested_role to make
                            a decision. */}
                        {/* ⚠️ THE DISAGREEMENT ROW. Origin set the owner; Eva's
                            classifier suggested something else. Neither is
                            automatically wrong, so BOTH are shown and the move
                            is a human decision — the disagreement being visible
                            is the whole point, because an invisible one just
                            means the ticket sits in the wrong tab forever. */}
                        {/* ⚠️ A HUMAN DECISION SILENCES THE CLASSIFIER. suggested_role
                            is what the topic map picked AT CREATION and never
                            changes — correctly, it is a point-in-time fact. But
                            once someone has deliberately moved the ticket, still
                            rendering "Eva suggests Client Success" next to a
                            Move button reads as a live recommendation to undo
                            what a person just did, offered by something that has
                            not seen any of the reasoning. owner_reason is set to
                            'transferred' by the transfer endpoint, so the ticket
                            already knows a human decided. */}
                        {t.owner_reason === 'transferred' ? (
                          t.suggested_role && t.suggested_role !== t.owner_role ? (
                            <div title={`Eva's classifier originally picked ${label(t.suggested_role)}. A person moved this since, so this is history rather than a recommendation.`}
                                 style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, marginTop: 2 }}>
                              Moved by a person · originally {label(t.suggested_role)}
                            </div>
                          ) : null
                        ) : t.suggested_role && t.owner_role && t.suggested_role !== t.owner_role ? (
                          <div style={{ fontSize: 10, marginTop: 3, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <span style={{ color: '#c026d3', fontWeight: 700 }}>
                              {label(t.owner_role)} · Eva suggests {label(t.suggested_role)}
                            </span>
                            <button
                              disabled={moving === t.id}
                              onClick={(e) => { e.stopPropagation(); move(t.id, t.suggested_role); }}
                              title={`Move ownership to ${label(t.suggested_role)}. Recorded as an audited transfer.`}
                              style={{ padding: '2px 8px', borderRadius: 6, border: '1px solid #c026d3',
                                background: '#fff', color: '#c026d3', fontSize: 10, fontWeight: 800,
                                cursor: moving === t.id ? 'wait' : 'pointer' }}>
                              {moving === t.id ? '…' : 'Move'}
                            </button>
                          </div>
                        ) : t.suggested_role ? (
                          <div title="What the topic map would pick. Advisory only — nothing acts on it."
                               style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, marginTop: 2 }}>
                            Suggested: {label(t.suggested_role)}
                          </div>
                        ) : null}
                      </td>
                      {/* ⚠️ THREE OWNER STATES, never collapsed into one word.
                          A person = accountable. "No <role> holder" = derived and
                          the role is empty (every borrower ticket today).
                          "No <role> on file" = the subject was never assigned, e.g.
                          an adversed file. "Not derived" = predates ownership. */}
                      <td style={{ padding: '10px', color: ownerToneColor(t.owner_state?.tone) }}>
                        {ownerText(t.owner_state, '—')}
                      </td>
                      <td style={{ padding: '10px', color: t.assignee_name ? '#111827' : '#6b7280' }}>{t.assignee_name || 'Unassigned'}</td>
                      <td style={{ padding: '10px', textTransform: 'capitalize' }}>{t.status}</td>
                      <td style={{ padding: '10px', fontWeight: 700, color: PRIORITY_COLOR[t.priority] || '#6b7280', textTransform: 'capitalize' }}>{t.priority}</td>
                      <td style={{ padding: '10px', whiteSpace: 'nowrap' }}>
                        <span style={{ fontWeight: 700, color: t.aging ? '#ff082f' : '#111827' }}>{ageText(t.age_hours)}</span>
                        {t.aging && <span title="Unassigned or untouched >24h" style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: '#ff082f', background: '#ffe4e6', padding: '1px 5px', borderRadius: 9999 }}>AGING</span>}
                      </td>
                      <td style={{ padding: '10px', color: '#9ca3af', whiteSpace: 'nowrap' }}>{t.updated_at ? new Date(t.updated_at).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── (1) FILTERED MESSAGE DETAIL ─────────────────────────────────────
          ⚠️ THE DATA WAS ALREADY STORED AND UNREADABLE. inbound_emails keeps the
          body and every header on every row — written before any matching is
          attempted, so a failed match is visible rather than silent — and this
          list showed a 300-character preview. Deciding whether the classifier
          was RIGHT about a message needs the message.

          ⚠️ HEADERS ARE THE POINT, NOT A BONUS. Every diagnosis this week turned
          on them: X-Auto-Response-Suppress explaining a suppressed reply, the SRS
          return-path proving the M365 redirect, the recipient proving which match
          tier fired. Reading them required a database session. */}
      {/* ── the declines, and what would close them ─────────────────────────
          ⚠️ THE ANALYSIS IS A READING, NOT A WRITE. Eva answers from published
          material; material published because a model proposed it, unreviewed,
          is how a mortgage company states something nobody approved. This
          proposes; a person writes. */}
      {/* ── LOG A CALL OR A DIRECT EMAIL ────────────────────────────────────
          ⚠️ IT GOES THROUGH THE SAME CREATION PATH AS EVERY OTHER TICKET, so a
          phoned-in request gets an owner, a reply token and a first-response
          clock like any other. A second creation path would produce rows that
          behave differently, and the differences leak out one surface at a
          time. */}
      {newTicket && (
        <div onClick={() => !creating && setNewTicket(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.45)', zIndex: 60,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 14, maxWidth: 560, width: '100%', maxHeight: '88vh', overflowY: 'auto', padding: 24 }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 800, color: '#111827' }}>Create ticket</h3>
            <p style={{ fontSize: 12.5, color: '#6b7280', margin: '0 0 16px', lineHeight: 1.6 }}>
              For anything that reached us outside the app — a phone call, or an email straight to
              someone at Doorly. It becomes an ordinary ticket: owned, replyable, and counted.
            </p>

            <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>How did it reach us</div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
              {[['phone', 'Phone call'], ['email_direct', 'Direct email'], ['in_person', 'In person'], ['other', 'Other']].map(([k, lbl]) => (
                <button key={k} onClick={() => setNewTicket((p) => ({ ...p, channel: k }))}
                  style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
                    border: `1.5px solid ${newTicket.channel === k ? BRAND.blue : '#e5e7eb'}`,
                    background: newTicket.channel === k ? '#edf1fb' : '#fff',
                    color: newTicket.channel === k ? BRAND.blue : '#6b7280' }}>{lbl}</button>
              ))}
            </div>

            {/* ⚠️ AN ACCOUNT **OR** A CONTACT. Attaching a stranger to a real
                account is refused outright by the creation path, and a ticket
                with no way to reach anyone cannot be replied to at all. */}
            <div style={{ display: 'flex', gap: 40, marginBottom: 18, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>Who</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[['borrower', 'Borrower'], ['partner', 'Partner']].map(([k, lbl]) => (
                    <button key={k} onClick={() => pickWho(k)}
                      style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
                        border: `1.5px solid ${newTicket.who === k ? BRAND.blue : '#e5e7eb'}`,
                        background: newTicket.who === k ? '#edf1fb' : '#fff',
                        color: newTicket.who === k ? BRAND.blue : '#6b7280' }}>{lbl}</button>
                  ))}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>Status</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[['existing', 'Existing'], ['prospective', 'Prospective']].map(([k, lbl]) => (
                    <button key={k} onClick={() => pickStanding(k)}
                      style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
                        border: `1.5px solid ${newTicket.standing === k ? BRAND.blue : '#e5e7eb'}`,
                        background: newTicket.standing === k ? '#edf1fb' : '#fff',
                        color: newTicket.standing === k ? BRAND.blue : '#6b7280' }}>{lbl}</button>
                  ))}
                </div>
              </div>
            </div>

            {/* ⚠️ EXISTING RESOLVES TO AN ACCOUNT; PROSPECTIVE CANNOT. The two
                are different kinds of requester, not one with a field missing —
                attaching a stranger to a real account is refused outright by the
                creation path, so the form does not offer the shape that would be
                refused. */}
            {/* ⚠️ THE NAME FIELD WAITS FOR BOTH ANSWERS, because it cannot be
                drawn before them: which one it is decides whether it searches
                borrowers or partners, and whether it searches at all. Rendering
                it early meant a field that changed meaning under the person
                using it — and a picker seeded to "borrower" quietly made that
                choice on their behalf, which is how a partner ends up searched
                for in the wrong table. Nothing is pre-chosen now. */}
            {!newTicket.who || !newTicket.standing ? (
              <div style={{ padding: '14px 16px', marginBottom: 14, borderRadius: 10,
                background: '#f9fafb', border: '1px dashed #e5e7eb', fontSize: 12.5, color: '#9ca3af' }}>
                Answer both above and the rest of the form appears.
              </div>
            ) : newTicket.standing === 'existing' ? (
              <div style={{ position: 'relative', marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>
                  {newTicket.who === 'partner' ? 'Partner' : 'Borrower'}
                </div>
                <input
                  placeholder={`Start typing a name${newTicket.who === 'borrower' ? ', email or loan number' : ' or email'}…`}
                  value={newTicket.contact_label || ''}
                  onFocus={() => setContactOpen(true)}
                  onChange={(e) => { setNewTicket((p) => ({ ...p, contact_label: e.target.value, user_id: null })); setContactOpen(true); }}
                  style={sel} />
                {contactOpen && (
                  <div style={{ position: 'absolute', zIndex: 5, left: 0, right: 0, background: '#fff',
                    border: '1px solid #e5e7eb', borderRadius: 8, marginTop: 2, maxHeight: 220, overflowY: 'auto',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.10)' }}>
                    {contacts.length === 0 && (
                      <div style={{ padding: '9px 12px', fontSize: 12.5, color: '#9ca3af' }}>No match.</div>
                    )}
                    {contacts.map((c) => (
                      <div key={c.id} onClick={() => {
                        setNewTicket((p) => ({ ...p, user_id: c.id, contact_label: c.name || c.email }));
                        setContactOpen(false);
                      }}
                        style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f9fafb' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#f9fafb'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}>
                        <div style={{ color: '#111827', fontWeight: 600 }}>{c.name || '(no name)'}</div>
                        <div style={{ color: '#9ca3af', fontSize: 11.5 }}>
                          {[c.email, c.loan_number, c.detail && label(c.detail)].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {newTicket.user_id && (
                  <div style={{ fontSize: 11, color: BRAND.teal, fontWeight: 700, marginTop: 4 }}>
                    ✓ Linked to account #{newTicket.user_id}
                  </div>
                )}
                {/* 🔑 ON BEHALF (push C): a partner ticket is filed FOR the
                    partner. She is the requester, gets the normal confirmation
                    and the replies; you are recorded as the one who filed it. */}
                {newTicket.who === 'partner' && newTicket.user_id && (
                  <div data-testid="on-behalf-note" style={{ fontSize: 12, color: '#374151', marginTop: 6 }}>
                    Submitting on behalf of <strong>{newTicket.contact_label}</strong>. They get the confirmation email and every reply.
                  </div>
                )}
              </div>
            ) : (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>
                  Prospective {newTicket.who}
                </div>
                {/* ⚠️ THREE INPUTS THAT FIT THE ROW. `sel` carries width:100% and
                    content-box padding, so three of them in a flex row measured
                    300% plus two gaps plus six paddings — the phone field ran
                    past the modal and put a scrollbar on it. flex-basis 0 with
                    minWidth 0 lets them share the row instead of each claiming
                    all of it; minWidth 0 is the part that matters, because a
                    flex item will not shrink below its content width without it. */}
                <div style={{ display: 'flex', gap: 8 }}>
                  {[['external_name', 'Name'], ['external_email', 'Email'], ['external_phone', 'Phone']].map(([k, ph]) => (
                    <input key={k} placeholder={ph} value={newTicket[k] || ''}
                      onChange={(e) => setNewTicket((p) => ({ ...p, [k]: e.target.value }))}
                      style={{ ...sel, flex: '1 1 0', minWidth: 0, width: 'auto', boxSizing: 'border-box' }} />
                  ))}
                </div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                  An email address is what makes the ticket replyable. Without one it can still be logged and worked, just not answered by email.
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>Topic</div>
                {/* ⚠️ THE LIST FOLLOWS WHO IT IS. A prospective borrower has no
                    file, so "adverse decision" and "incompleteness request" are
                    about something they do not have — and a topic picked in
                    error routes the ticket to a team with nothing to work with. */}
                <select style={sel} value={newTicket.topic}
                  onChange={(e) => setNewTicket((p) => ({ ...p, topic: e.target.value }))}>
                  {topicsFor(newTicket.who, newTicket.standing).map((t) => <option key={t} value={t}>{label(t)}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>Priority</div>
                <select style={sel} value={newTicket.priority}
                  onChange={(e) => setNewTicket((p) => ({ ...p, priority: e.target.value }))}>
                  <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                </select>
              </div>
            </div>

            <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>What they need</div>
            {/* width:100% AND box-sizing — a textarea defaults to content-box,
                so padding pushed it past the modal's inner width and it sat a
                few pixels short of every field above it. */}
            <textarea rows={5} placeholder="In their words where you can. This is what the next person reads."
              value={newTicket.summary || ''} onChange={(e) => setNewTicket((p) => ({ ...p, summary: e.target.value }))}
              style={{ ...sel, width: '100%', boxSizing: 'border-box', display: 'block', resize: 'vertical', lineHeight: 1.6 }} />

            {newTicket.error && (
              <div style={{ marginTop: 10, padding: '9px 12px', background: '#fff0f3', border: '1px solid #ffc5ca',
                borderRadius: 8, fontSize: 13, color: BRAND.error }}>{newTicket.error}</div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button onClick={() => setNewTicket(null)} disabled={creating}
                style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff',
                  color: '#6b7280', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
              {/* Disabled until the form knows who this is — the endpoint would
                  refuse it anyway, and refusing in the button is a better place
                  to find out than in a red banner after typing everything. */}
              <button onClick={createManualTicket} disabled={creating || !newTicket.who || !newTicket.standing}
                style={{ flex: 1, padding: '9px 14px', borderRadius: 8, border: 'none', background: BRAND.blue,
                  color: '#fff', fontSize: 13, fontWeight: 800,
                  cursor: creating ? 'wait' : 'pointer',
                  opacity: (creating || !newTicket.who || !newTicket.standing) ? 0.5 : 1 }}>
                {creating ? 'Creating…' : 'Create ticket'}
              </button>
            </div>
          </div>
        </div>
      )}

      {declines && (
        <div onClick={() => setDeclines(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.45)', zIndex: 60,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 14, maxWidth: 780, width: '100%',
              maxHeight: '85vh', overflowY: 'auto', padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#111827' }}>
                {String(declines.reason).replace(/_/g, ' ')} — {declines.messages.length} message{declines.messages.length === 1 ? '' : 's'}
              </h3>
              <button onClick={() => setDeclines(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 20 }}>×</button>
            </div>
            <p style={{ fontSize: 12.5, color: '#6b7280', margin: '0 0 16px', lineHeight: 1.6 }}>
              {NO_ATTEMPT_COPY[declines.reason] || ''}
            </p>

            {declines.loading && <div style={{ color: '#9ca3af', fontSize: 13 }}>Loading…</div>}
            {declines.error && <div style={{ color: BRAND.error, fontSize: 13 }}>{declines.error}</div>}

            {!declines.loading && declines.messages.length === 0 && !declines.error && (
              <div style={{ color: '#9ca3af', fontSize: 13 }}>Nothing in this category yet.</div>
            )}

            {declines.messages.map((m) => (
              <div key={m.id} style={{ border: `1px solid ${BRAND.slateLight}`, borderRadius: 10,
                padding: '10px 14px', marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
                  {m.from_name || m.from_email} · {m.received_at ? new Date(m.received_at).toLocaleString() : '—'}
                </div>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: '#111827' }}>{m.subject || '(no subject)'}</div>
                <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap', marginTop: 6, lineHeight: 1.6 }}>
                  {String(m.body || '').slice(0, 600)}
                </div>
              </div>
            ))}

            {declines.messages.length > 0 && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${BRAND.slateLight}` }}>
                <button onClick={analyseDeclines} disabled={declines.analysing}
                  style={{ padding: '9px 14px', background: BRAND.blue, color: '#fff', border: 'none',
                    borderRadius: 8, fontSize: 13, fontWeight: 700,
                    cursor: declines.analysing ? 'not-allowed' : 'pointer', opacity: declines.analysing ? 0.6 : 1 }}>
                  {declines.analysing ? 'Analysing…' : 'What would let Eva answer these?'}
                </button>
                {declines.analysis && (
                  <pre style={{ marginTop: 14, whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.7,
                    color: '#111827', fontFamily: 'inherit', background: '#f9fafb',
                    border: `1px solid ${BRAND.slateLight}`, borderRadius: 10, padding: 14 }}>{declines.analysis}</pre>
                )}
                <div style={{ marginTop: 8, fontSize: 11, color: '#9ca3af', lineHeight: 1.5 }}>
                  A proposal for a person to review — nothing is published to the knowledge base from here.
                  Not every decline is a gap: some of these Eva should keep declining.
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {msgDetail && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }} onClick={() => setMsgDetail(null)} />
          <div style={{ position: 'relative', background: '#fff', borderRadius: 14, width: 'min(760px, 100%)', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 12px 48px rgba(0,0,0,0.25)' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: '#fff' }}>
              <strong style={{ fontSize: 14, color: '#111827' }}>Filtered message</strong>
              <button onClick={() => setMsgDetail(null)} style={{ background: 'none', border: 'none', fontSize: 18, color: '#9ca3af', cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 18 }}>
              {msgLoading && <div style={{ color: '#9ca3af', fontSize: 13 }}>Loading…</div>}
              {msgDetail.error && <div style={{ color: '#ff082f', fontSize: 13 }}>{msgDetail.error}</div>}
              {!msgLoading && !msgDetail.error && msgDetail.subject !== undefined && (
                <>
                  <div style={{ fontSize: 13, color: '#374151', marginBottom: 12, lineHeight: 1.8 }}>
                    <div><strong>From:</strong> {msgDetail.from_name ? `${msgDetail.from_name} <${msgDetail.from_email}>` : msgDetail.from_email}</div>
                    <div><strong>To:</strong> {msgDetail.to_emails || '—'}</div>
                    <div><strong>Subject:</strong> {msgDetail.subject || '(none)'}</div>
                    <div><strong>Received:</strong> {msgDetail.received_at ? new Date(msgDetail.received_at).toLocaleString() : '—'}</div>
                    {/* Why it was classified this way, and what Eva did — the two
                        facts that decide whether the filter was right. */}
                    <div><strong>Disposition:</strong> {msgDetail.disposition}{msgDetail.match_method ? ` (${msgDetail.match_method})` : ''}</div>
                    <div><strong>Eva:</strong> {msgDetail.eva_outcome || 'not invoked'}</div>
                    {msgDetail.ticket_number && <div><strong>Ticket:</strong> {msgDetail.ticket_number}</div>}
                    {msgDetail.account_match_note && (
                      <div style={{ marginTop: 6, fontSize: 12, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '6px 10px' }}>
                        {msgDetail.account_match_note}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '14px 0 6px' }}>Body</div>
                  {/* ⚠️ TEXT BODY, NEVER THE HTML. Rendering a stranger's HTML in
                      DoorlyDesk would execute their markup in a staff session;
                      this is unsolicited mail by definition. */}
                  <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12.5, color: '#111827', background: '#f9fafb', border: '1px solid #eef2f7', borderRadius: 8, padding: 12, margin: 0, fontFamily: 'ui-monospace, monospace' }}>
                    {msgDetail.text_body || '(no plain-text body — the sender supplied HTML only)'}
                  </pre>
                  <div style={{ fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '16px 0 6px' }}>
                    Headers ({(msgDetail.headers || []).length})
                  </div>
                  <div style={{ border: '1px solid #eef2f7', borderRadius: 8, overflow: 'hidden' }}>
                    {(msgDetail.headers || []).map(([k, v]) => (
                      <div key={k} style={{ display: 'flex', gap: 10, padding: '5px 10px', borderTop: '1px solid #f3f4f6', fontSize: 11.5, fontFamily: 'ui-monospace, monospace' }}>
                        <span style={{ color: '#6b7280', minWidth: 190, flexShrink: 0, wordBreak: 'break-all' }}>{k}</span>
                        <span style={{ color: '#111827', wordBreak: 'break-all' }}>{v}</span>
                      </div>
                    ))}
                    {!(msgDetail.headers || []).length && (
                      <div style={{ padding: '8px 10px', fontSize: 12, color: '#9ca3af' }}>
                        No headers stored — the receiving API did not return them for this message.
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
